import React, { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppBar, Toolbar, Typography, Button, Box, IconButton, Menu, MenuItem, useMediaQuery, useTheme, Avatar
} from '@mui/material';
import { ExitToApp, Menu as MenuIcon, AccountCircle } from '@mui/icons-material';
import { logout } from '../store/authSlice';
import MobileNavigation from './MobileNavigation';
import MobileFab from './MobileFab';

const Layout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
    } catch (error) {
      console.warn('Logout error:', error);
    } finally {
      navigate('/login');
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    handleMenuClose();
  };

  const getMenuItems = () => {
    const baseItems = [
      { text: 'Dashboard', path: '/' }
    ];

    if (['Admin', 'Sample Collector'].includes(user?.role)) {
      const items = [
        ...baseItems,
        { text: 'Samples', path: '/samples' }
      ];
      
      // Only Admin can access user management
      if (user?.role === 'Admin') {
        items.push({ text: 'Users', path: '/users' });
        items.push({ text: 'Utility', path: '/dropdowns' });
      }
      
      return items;
    }

    if (user?.role === 'HOD' || user?.role === 'Tester') {
      return [
        ...baseItems,
        { text: 'My Samples', path: '/samples' }
      ];
    }

    return baseItems;
  };

  const menuItems = getMenuItems();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ zIndex: 1201 }}>
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          <Typography 
            variant="h6" 
            sx={{ 
              flexGrow: 1, 
              fontSize: { xs: '1rem', md: '1.25rem' },
              fontWeight: 600
            }}
          >
            Sample Movement Register
          </Typography>
          
          {isMobile ? (
            <>
              <Avatar 
                sx={{ 
                  width: 32, 
                  height: 32, 
                  bgcolor: 'secondary.main',
                  fontSize: '0.875rem',
                  mr: 1
                }}
              >
                {user?.name?.charAt(0)?.toUpperCase()}
              </Avatar>
              <IconButton color="inherit" onClick={handleMenuOpen}>
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 200,
                    borderRadius: 2
                  }
                }}
              >
                <MenuItem disabled>
                  <Box>
                    <Typography variant="subtitle2">{user?.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {user?.role}{user?.department && ` - ${user.department}`}
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <ExitToApp sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <>
              {menuItems.map((item) => (
                <Button 
                  key={item.text}
                  color="inherit" 
                  onClick={() => navigate(item.path)}
                  sx={{ mx: 1 }}
                >
                  {item.text}
                </Button>
              ))}
              <Typography variant="body2" sx={{ mx: 2 }}>
                {user?.name} ({user?.role})
                {user?.department && ` - ${user.department}`}
              </Typography>
              <Button color="inherit" onClick={handleLogout} startIcon={<ExitToApp />}>
                Logout
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>

      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 }, 
          mt: { xs: 7, sm: 8 },
          mb: { xs: 8, md: 0 }, // Bottom margin for mobile navigation
          backgroundColor: 'background.default',
          minHeight: 'calc(100vh - 64px)'
        }}
      >
        <Outlet />
      </Box>
      
      <MobileNavigation />
      <MobileFab />
    </Box>
  );
};

export default Layout;